import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '@clerk/clerk-react';

export const Home = () => {
  const { isSignedIn } = useUser();

  return (
    <div className="pt-20 bg-black min-h-screen">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-gray-900 to-black overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1566577739112-5180d4bf9390?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80" 
            alt="NFL Football Stadium" 
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/70"></div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight drop-shadow-lg">
              Fantasy Football
              <span className="block text-fiesta-blue">Made Simple</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto mb-10 leading-relaxed drop-shadow">
              Get the tools you need to make smarter fantasy decisions. Player rankings, trade analysis, and draft help all in one place.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/players"
                className="px-8 py-4 bg-fiesta-blue hover:bg-fiesta-blue/80 text-white font-semibold rounded-lg transition-colors text-lg shadow-lg"
              >
                View Player Rankings
              </Link>
              <Link
                to="/trade-calculator"
                className="px-8 py-4 border-2 border-gray-500 text-gray-200 hover:text-white hover:border-gray-400 font-semibold rounded-lg transition-colors text-lg backdrop-blur-sm bg-black/20"
              >
                Try Trade Calculator
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 bg-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Everything You Need to Win
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Simple tools to help you make better fantasy football decisions
            </p>
          </div>

          <div className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                name: 'Player Rankings',
                description: 'Updated weekly rankings with key stats and insights for every player.',
                icon: '📊',
                link: '/players'
              },
              {
                name: 'Trade Calculator',
                description: 'Evaluate trades to see if you\'re getting fair value for your players.',
                icon: '⚖️',
                link: '/trade-calculator'
              },
              {
                name: 'Draft Assistant',
                description: 'Get personalized picks based on your league settings and draft position.',
                icon: '🎯',
                link: '/draft-assistant'
              },
              {
                name: 'Roster Manager',
                description: 'Track your team and get weekly start/sit recommendations.',
                icon: '👥',
                link: '/my-roster'
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Link to={feature.link} className="block h-full group">
                  <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 h-full hover:border-gray-700 transition-colors">
                    <div className="text-3xl mb-4">{feature.icon}</div>
                    <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-fiesta-blue transition-colors">
                      {feature.name}
                    </h3>
                    <p className="text-gray-400 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Simple CTA Section - Only show if user is not signed in */}
      {!isSignedIn && (
        <div className="py-20 bg-gray-900">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to improve your fantasy game?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Start using our tools today and make smarter fantasy decisions.
            </p>
            <Link
              to="/players"
              className="inline-block px-8 py-4 bg-fiesta-blue hover:bg-fiesta-blue/80 text-white font-semibold rounded-lg transition-colors text-lg"
            >
              Get Started Free
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};
